import type { HttpError } from 'http-errors';

/**
 * API Errors reference: https://0x.org/docs/api
 */

/**
 * Details of an error returned from the 0x API.
 */
export interface ApiErrorDetails {
  /** Unique name/key of the error */
  name: string;
  /** What went wrong */
  message: string;
  /** Data associated with the error */
  data: Record<string, unknown>;
}

/**
 * Context of an error returned from the 0x API. This will include event data
 * used for error reporting purposes.
 */
export type ErrorContext = Record<string, unknown>;

/**
 * Parent class to handle API errors with details.
 */
export class ApiError extends Error implements Omit<HttpError, 'expose'> {
  public context?: ErrorContext;
  public details: ApiErrorDetails;
  public status: number;
  public statusCode: number;

  constructor(status: number, details: ApiErrorDetails, context?: ErrorContext) {
    super(details.message);
    this.name = 'ApiError';
    this.details = details;
    this.status = status;
    this.statusCode = status;

    // optional context
    if (context) this.context = context;

    // Set the prototype explicitly to maintain instanceof checks when
    // targeting older JavaScript runtimes.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * `BUY_TOKEN_NOT_AUTHORIZED_FOR_TRADE` errors.
 */
export class ApiBuyTokenUnauthorizedForTradeError extends ApiError {
  public name = 'ApiBuyTokenUnauthorizedForTradeError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(422, details, context);
  }
}

/**
 * `INPUT_INVALID` errors.
 */
export class ApiInputInvalidError extends ApiError {
  public name = 'ApiInputInvalidError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `INSUFFICIENT_BALANCE` errors.
 */
export class ApiInsufficientBalanceError extends ApiError {
  public name = 'ApiInsufficientBalanceError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `INSUFFICIENT_BALANCE_OR_ALLOWANCE` errors.
 */
export class ApiInsufficientBalanceOrAllowanceError extends ApiError {
  public name = 'ApiInsufficientBalanceOrAllowanceError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `INTERNAL_SERVER_ERROR` errors.
 */
export class ApiInternalServerError extends ApiError {
  public name = 'ApiInternalServerError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(500, details, context);
  }
}

/**
 * `INVALID_SIGNATURE` errors.
 */
export class ApiInvalidSignatureError extends ApiError {
  public name = 'ApiInvalidSignatureError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `INVALID_SIGNER` errors.
 */
export class ApiInvalidSignerError extends ApiError {
  public name = 'ApiInvalidSignerError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `TRADE_EXPIRY_TOO_SOON` errors (formerly `META_TRANSACTION_EXPIRY_TOO_SOON`).
 */
export class ApiMetaTransactionExpiryTooSoonError extends ApiError {
  public name = 'ApiMetaTransactionExpiryTooSoonError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `TRADE_INVALID` errors (formerly `META_TRANSACTION_INVALID`).
 */
export class ApiMetaTransactionInvalidError extends ApiError {
  public name = 'ApiMetaTransactionInvalidError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `TRADE_STATUS_NOT_FOUND` errors (formerly `META_TRANSACTION_STATUS_NOT_FOUND`).
 */
export class ApiMetaTransactionStatusNotFoundError extends ApiError {
  public name = 'ApiMetaTransactionStatusNotFoundError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(404, details, context);
  }
}

/**
 * `PENDING_TRADES_ALREADY_EXIST` errors.
 */
export class ApiPendingTradesAlreadyExistError extends ApiError {
  public name = 'ApiPendingTradesAlreadyExistError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `SELL_AMOUNT_TOO_SMALL` errors.
 */
export class ApiSellAmountTooSmallError extends ApiError {
  public name = 'ApiSellAmountTooSmallError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `SELL_TOKEN_NOT_AUTHORIZED_FOR_TRADE` errors.
 */
export class ApiSellTokenUnauthorizedForTradeError extends ApiError {
  public name = 'ApiSellTokenUnauthorizedForTradeError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(422, details, context);
  }
}

/**
 * `SWAP_VALIDATION_FAILED` errors.
 */
export class ApiSwapValidationFailedError extends ApiError {
  public name = 'ApiSwapValidationFailedError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `TAKER_NOT_AUTHORIZED_FOR_TRADE` errors.
 */
export class ApiTakerNotAuthorizedForTrade extends ApiError {
  public name = 'ApiTakerNotAuthorizedForTrade';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(403, details, context);
  }
}

/**
 * `TOKEN_NOT_SUPPORTED` errors.
 */
export class ApiTokenNotSupportedError extends ApiError {
  public name = 'ApiTokenNotSupportedError';

  constructor(details: ApiErrorDetails, context?: ErrorContext) {
    super(400, details, context);
  }
}

/**
 * `UNCATEGORIZED` errors.
 */
export class ApiUncategorizedError extends ApiError {
  public name = 'ApiUncategorizedError';

  constructor(details: ApiErrorDetails, context?: ErrorContext, status = 500) {
    super(status, details, context);
  }
}

/**
 * No liquidity available error
 */
export class ApiNoLiquidityAvailableError extends ApiError {
  public name = 'ApiNoLiquidityAvailableError';

  constructor() {
    super(
      204,
      { name: 'NO_LIQUIDITY_AVAILABLE', message: 'No liquidity available', data: {} },
      undefined,
    );
  }
}

/**
 * Type for any constructor of a class that extends ApiError.
 */
type ApiErrorConstructor<T extends ApiError> = new (
  details: ApiErrorDetails,
  context?: ErrorContext,
) => T;

/**
 * Map of error codes to error constructors.
 *
 * The intents API renamed its meta-transaction codes to `TRADE_*` in July 2026;
 * the legacy names are kept as a fallback.
 */
const codeToError: Record<string, ApiErrorConstructor<ApiError>> = {
  BUY_TOKEN_NOT_AUTHORIZED_FOR_TRADE: ApiBuyTokenUnauthorizedForTradeError,
  INPUT_INVALID: ApiInputInvalidError,
  INSUFFICIENT_BALANCE: ApiInsufficientBalanceError,
  INSUFFICIENT_BALANCE_OR_ALLOWANCE: ApiInsufficientBalanceOrAllowanceError,
  INTERNAL_SERVER_ERROR: ApiInternalServerError,
  INVALID_SIGNATURE: ApiInvalidSignatureError,
  INVALID_SIGNER: ApiInvalidSignerError,
  META_TRANSACTION_EXPIRY_TOO_SOON: ApiMetaTransactionExpiryTooSoonError,
  META_TRANSACTION_INVALID: ApiMetaTransactionInvalidError,
  META_TRANSACTION_STATUS_NOT_FOUND: ApiMetaTransactionStatusNotFoundError,
  PENDING_TRADES_ALREADY_EXIST: ApiPendingTradesAlreadyExistError,
  SELL_AMOUNT_TOO_SMALL: ApiSellAmountTooSmallError,
  SELL_TOKEN_NOT_AUTHORIZED_FOR_TRADE: ApiSellTokenUnauthorizedForTradeError,
  SWAP_VALIDATION_FAILED: ApiSwapValidationFailedError,
  TAKER_NOT_AUTHORIZED_FOR_TRADE: ApiTakerNotAuthorizedForTrade,
  TOKEN_NOT_SUPPORTED: ApiTokenNotSupportedError,
  TRADE_EXPIRY_TOO_SOON: ApiMetaTransactionExpiryTooSoonError,
  TRADE_INVALID: ApiMetaTransactionInvalidError,
  TRADE_STATUS_NOT_FOUND: ApiMetaTransactionStatusNotFoundError,
  UNCATEGORIZED: ApiUncategorizedError,
};

/**
 * Create an instance of an API error based on the error key and details.
 * Defaults to uncategorized error if the error key is not recognized.
 *
 * For the uncategorized fallback the real HTTP `status` is preserved instead of
 * defaulting to 500, so an unrecognized 4xx relayer rejection isn't misclassified
 * as a 5xx (which downstream maps to `relayer_unavailable`).
 */
export const createApiError = (
  details: ApiErrorDetails,
  { context, status }: { context?: ErrorContext; status?: number } = {},
): ApiError => {
  const ErrorClass = codeToError[details.name];
  if (!ErrorClass || ErrorClass === ApiUncategorizedError) {
    return new ApiUncategorizedError(details, context, status);
  }
  return new ErrorClass(details, context);
};
